/* Shift: "splash to switch". Draws the dry photo, then reveals the matching wet photo through
   splash-shaped patches that spread out, stay wet for a moment and then fade back to dry together.
   Plays by itself when it scrolls into view; visitors can tap or drag across the shorts to splash it.
   Markup: <shift-splash data-autoplay data-cycle data-start="0"> with a JSON list of pairs. */
(function () {
  if (customElements.get('shift-splash')) return;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ease(t) { return 1 - Math.pow(1 - t, 3); }
  function rand(a, b) { return a + Math.random() * (b - a); }
  var HOLD = 2600, FADE = 1300; // stay wet, then the whole patch fades back to dry together

  class ShiftSplash extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var data = this.querySelector('script[type="application/json"]');
      this.pairs = data ? JSON.parse(data.textContent) : [];
      if (!this.pairs.length) return;
      this.stage = this.querySelector('.shift-splash__stage');
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.mask = document.createElement('canvas');
      this.mctx = this.mask.getContext('2d');
      this.layer = document.createElement('canvas');
      this.lctx = this.layer.getContext('2d');
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-splash-tab]'));
      this.state = this.querySelector('[data-splash-state]');
      this.cta = this.querySelector('[data-splash-cta]');
      this.hint = this.querySelector('.shift-splash__hint');
      this.autoplay = this.hasAttribute('data-autoplay') && !reduced;
      this.cycle = this.hasAttribute('data-cycle');
      this.index = Math.max(0, Math.min(this.pairs.length - 1, parseInt(this.dataset.start || '0', 10) || 0));
      this.drops = [];
      this.wetUntil = 0;
      this.touched = false;
      this.visible = false;
      this.imgs = {};
      this.phase = 'idle';
      this.phaseAt = 0;
      this.delayLeft = parseInt(this.dataset.delay || '0', 10) || 0;

      var self = this;
      this.tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { self.touched = true; self.show(i); self.demo(); });
      });
      this.canvas.addEventListener('pointerdown', function (e) { self.onPoint(e, true); });
      this.canvas.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'mouse' && !e.buttons) return;
        self.onPoint(e, false);
      });
      if ('ResizeObserver' in window) new ResizeObserver(function () { self.resize(); }).observe(this.stage);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          self.visible = entries[0].isIntersecting;
          if (self.visible) { self.kick(); if (self.autoplay && !self.touched && self.phase === 'idle') self.startAuto(); }
        }, { threshold: 0.35 }).observe(this.stage);
      } else { this.visible = true; }
      document.addEventListener('visibilitychange', function () { if (!document.hidden) self.kick(); });
      this.show(this.index);
      this.resize();
    }

    load(src) {
      var self = this;
      if (this.imgs[src]) return this.imgs[src];
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () { self.kick(); };
      img.src = src;
      this.imgs[src] = img;
      return img;
    }

    show(i) {
      this.index = i;
      var p = this.pairs[i];
      this.dry = this.load(p.dry);
      this.wet = this.load(p.wet);
      var next = this.pairs[(i + 1) % this.pairs.length];
      this.load(next.dry); this.load(next.wet);
      this.mctx.clearRect(0, 0, this.mask.width, this.mask.height);
      this.drops = [];
      this.wetUntil = 0;
      this.hasWet = false;
      this.tabs.forEach(function (t, k) { t.setAttribute('aria-selected', k === i ? 'true' : 'false'); });
      if (this.cta && p.link) {
        this.cta.href = p.link;
        var label = this.cta.getAttribute('data-label') || '';
        this.cta.querySelector('span').textContent = label.replace('[colour]', p.name).replace('[color]', p.name);
      }
      this.setState(false);
      this.kick();
    }

    setState(wet) {
      var p = this.pairs[this.index];
      this.isWet = wet;
      this.style.setProperty('--splash-now', wet ? p.wetColour : p.dryColour);
      this.style.setProperty('--splash-dry', p.dryColour);
      this.style.setProperty('--splash-wet', p.wetColour);
      var host = this.closest('[data-splash-host]');
      if (host) host.style.setProperty('--splash-now', wet ? p.wetColour : p.dryColour);
      if (this.state) {
        this.state.querySelector('b').textContent = wet ? this.state.dataset.wet : this.state.dataset.dry;
        this.state.querySelector('em').textContent = wet ? p.wetName : p.dryName;
      }
    }

    resize() {
      var w = this.stage.clientWidth, h = this.stage.clientHeight;
      if (!w || !h) return;
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      var W = Math.round(w * dpr), H = Math.round(h * dpr);
      if (W === this.canvas.width && H === this.canvas.height) return;
      [this.canvas, this.mask, this.layer].forEach(function (c) { c.width = W; c.height = H; });
      this.drops = [];
      this.hasWet = false;
      this.kick();
    }

    onPoint(e, first) {
      var r = this.canvas.getBoundingClientRect();
      var x = (e.clientX - r.left) / r.width * this.canvas.width;
      var y = (e.clientY - r.top) / r.height * this.canvas.height;
      var now = performance.now();
      if (!first && this.lastPt && Math.hypot(x - this.lastPt.x, y - this.lastPt.y) < this.canvas.width * 0.07) return;
      if (!first && now - (this.lastAt || 0) < 50) return;
      this.lastPt = { x: x, y: y }; this.lastAt = now;
      this.touched = true;
      this.phase = 'idle';
      this.splash(x, y, this.canvas.width * (first ? rand(0.16, 0.2) : rand(0.09, 0.12)));
      if (this.hint) this.hint.classList.add('is-gone');
    }

    splash(x, y, R) {
      // a solid core, a ragged rim of overlapping blobs, splatter flung outwards and a couple of drips
      var blobs = [{ dx: 0, dy: 0, r: R * 0.72, core: true }];
      var n = 26 + Math.floor(Math.random() * 10);
      for (var i = 0; i < n; i++) {
        var a = (i / n) * Math.PI * 2 + rand(-0.12, 0.12), d = rand(0.5, 0.92) * R;
        blobs.push({ dx: Math.cos(a) * d, dy: Math.sin(a) * d, r: rand(0.07, 0.26) * R });
      }
      var m = 22 + Math.floor(Math.random() * 18);
      for (var j = 0; j < m; j++) {
        var b = rand(0, Math.PI * 2), e = rand(1.0, 1.9) * R;
        blobs.push({ dx: Math.cos(b) * e, dy: Math.sin(b) * e, r: rand(0.015, 0.05) * R, late: true, ang: b, stretch: rand(1, 1.5) });
      }
      for (var k = 0; k < 2; k++) {
        blobs.push({ dx: rand(-0.5, 0.5) * R, dy: R * rand(0.7, 1.1), r: rand(0.07, 0.11) * R, drip: true, len: R * rand(0.35, 0.8) });
      }
      var a = this.wetAlpha(performance.now());
      if (a < 1) {
        // a new splash during the fade keeps whatever is still showing, then everything fades together again
        this.mctx.globalCompositeOperation = 'destination-in';
        this.mctx.fillStyle = 'rgba(0,0,0,' + a.toFixed(3) + ')';
        this.mctx.fillRect(0, 0, this.mask.width, this.mask.height);
        this.mctx.globalCompositeOperation = 'source-over';
      }
      this.drops.push({ x: x, y: y, blobs: blobs, t0: performance.now(), dur: 420 });
      this.hasWet = true;
      this.wetUntil = performance.now() + HOLD;
      this.setState(true);
      this.kick();
    }

    paintDrop(d, p) {
      var c = this.mctx;
      for (var i = 0; i < d.blobs.length; i++) {
        var b = d.blobs[i];
        var q = b.late ? Math.max(0, (p - 0.3) / 0.7) : p;
        if (q <= 0) continue;
        var spread = b.late ? 1 : (0.45 + 0.55 * q);
        var r = b.r * (b.late ? 1 : (0.5 + 0.5 * q));
        var cx = d.x + b.dx * spread;
        var cy = d.y + b.dy * spread;
        c.save();
        c.translate(cx, cy);
        if (b.late) { c.rotate(b.ang); c.scale(b.stretch, 1); }
        if (b.drip) { c.scale(1, 1 + (b.len / b.r) * q); }
        var g = c.createRadialGradient(0, 0, r * (b.core ? 0.86 : 0.7), 0, 0, r);
        g.addColorStop(0, 'rgba(0,0,0,1)');
        g.addColorStop(1, 'rgba(0,0,0,0)');
        c.fillStyle = g;
        c.beginPath(); c.arc(0, 0, r, 0, Math.PI * 2); c.fill();
        c.restore();
      }
    }

    wetAlpha(now) {
      // 1 while wet, then eases to 0 over FADE; driven by the clock so it always finishes
      if (!this.hasWet || now <= this.wetUntil) return 1;
      var t = (now - this.wetUntil) / FADE;
      return t >= 1 ? 0 : Math.pow(1 - t, 2.5);
    }

    startAuto() {
      this.phase = 'splashing';
      this.phaseAt = performance.now() + this.delayLeft; // only the first run waits
      this.delayLeft = 0;
      this.autoCount = 0;
      this.kick();
    }

    demo() {
      // one splash so a tab change shows the switch straight away
      var W = this.canvas.width, H = this.canvas.height;
      this.splash(W * rand(0.38, 0.62), H * rand(0.42, 0.62), W * 0.2);
    }

    auto(now) {
      if (!this.autoplay || this.touched || !this.visible) return;
      var W = this.canvas.width, H = this.canvas.height;
      if (this.phase === 'splashing') {
        if (now - this.phaseAt > 700 + this.autoCount * 420 && this.autoCount < 3) {
          var spots = [[0.36, 0.5], [0.6, 0.38], [0.52, 0.7]];
          var s = spots[this.autoCount];
          this.splash(W * (s[0] + rand(-0.04, 0.04)), H * (s[1] + rand(-0.04, 0.04)), W * rand(0.17, 0.22));
          this.autoCount++;
        }
        if (this.autoCount >= 3 && now > this.wetUntil + FADE + 500) {
          this.phase = 'next'; this.phaseAt = now;
        }
      } else if (this.phase === 'next') {
        if (now - this.phaseAt > 600) {
          if (this.cycle && this.pairs.length > 1) this.show((this.index + 1) % this.pairs.length);
          this.startAuto();
        }
      }
    }

    kick() {
      if (this._raf) return;
      var self = this;
      this._raf = requestAnimationFrame(function (t) { self._raf = null; self.frame(t); });
    }

    drawCover(ctx, img) {
      var W = ctx.canvas.width, H = ctx.canvas.height;
      var iw = img.naturalWidth, ih = img.naturalHeight;
      var s = Math.max(W / iw, H / ih);
      var sw = W / s, sh = H / s;
      ctx.drawImage(img, (iw - sw) / 2, (ih - sh) / 2, sw, sh, 0, 0, W, H);
    }

    frame(now) {
      var busy = false;
      // spread new splashes
      for (var i = this.drops.length - 1; i >= 0; i--) {
        var d = this.drops[i];
        var p = Math.max(0, Math.min(1, (now - d.t0) / d.dur));
        this.paintDrop(d, ease(p));
        if (p >= 1) this.drops.splice(i, 1); else busy = true;
      }
      // once the splashing stops, the whole wet patch fades back to dry at the same time
      var alpha = this.wetAlpha(now);
      if (this.hasWet && alpha <= 0 && !this.drops.length) {
        this.mctx.clearRect(0, 0, this.mask.width, this.mask.height);
        this.hasWet = false;
      }
      if (this.isWet && (!this.hasWet || alpha < 0.45)) this.setState(false);
      if (this.hasWet) busy = true;
      // compose
      var ok = this.dry && this.dry.complete && this.dry.naturalWidth && this.wet && this.wet.complete && this.wet.naturalWidth;
      if (ok) {
        this.drawCover(this.ctx, this.dry);
        if (this.hasWet) {
          this.lctx.globalCompositeOperation = 'source-over';
          this.lctx.clearRect(0, 0, this.layer.width, this.layer.height);
          this.drawCover(this.lctx, this.wet);
          this.lctx.globalCompositeOperation = 'destination-in';
          this.lctx.drawImage(this.mask, 0, 0);
          this.ctx.globalAlpha = alpha;
          this.ctx.drawImage(this.layer, 0, 0);
          this.ctx.globalAlpha = 1;
        }
        this.stage.classList.add('is-ready');
      }
      this.auto(now);
      if (this.autoplay && !this.touched && this.visible) busy = true;
      if (busy && this.visible) this.kick();
    }
  }
  customElements.define('shift-splash', ShiftSplash);
})();
